import {
  foodSourceDetailLines,
  foodSourceGroupLabel,
  foodSourceHasAutomatedRunner,
} from "../../../../constants/foodSourceConstants";
import { transportSourceDetailLines } from "../../../../constants/transportSourceConstants";
import {
  deriveComplianceDisplay,
  deriveRegistryBadge,
  deriveVehicleMarketActionState,
  formatSourceTypeLabel,
  isVehicleOnlyRow,
  listingsDisplay,
  normalizeVehicleMarketHealthKey,
} from "../automotive/vehicleMarketSourceFormat";

function formatDuration(ms) {
  if (ms == null || Number.isNaN(ms)) {
    return null;
  }
  if (ms < 1000) {
    return `${ms}ms`;
  }
  const seconds = Math.round(ms / 1000);
  if (seconds < 60) {
    return `${seconds}s`;
  }
  const minutes = Math.floor(seconds / 60);
  const remainder = seconds % 60;
  return `${minutes}m ${remainder}s`;
}

export function normalizeGridHealth(source) {
  if (!source.enabled && !source.isActive) {
    return "disabled";
  }

  const latestStatus = String(source.latestRun?.status || source.lastStatus || source.healthStatus || "").toLowerCase();

  if (
    source.isRunning ||
    source.health === "running" ||
    latestStatus === "running" ||
    latestStatus === "queued"
  ) {
    return latestStatus === "queued" ? "queued" : "running";
  }

  if (latestStatus === "failed" || source.health === "failed" || source.healthStatus === "failed" || source.healthStatus === "failing") {
    return "failed";
  }

  if (source.health === "healthy" || source.healthStatus === "healthy") {
    return "healthy";
  }

  if (source.health === "warning" || source.healthStatus === "warning" || source.healthStatus === "stale") {
    return "warning";
  }

  return source.health || source.healthStatus || (source.latestRun || source.lastRunAt ? "warning" : "never_run");
}

function withCommonFields(source, extras = {}) {
  const sourceKey = extras.sourceKey || source.sourceKey || source.sourceCode;
  const latest = extras.latestRun || source.latestRun || null;
  return {
    ...source,
    sourceKey,
    displayName: extras.displayName || source.displayName || source.sourceName,
    sourceCode: extras.sourceCode || source.sourceCode || source.sourceKey,
    enabled: extras.enabled ?? source.enabled ?? source.isActive ?? false,
    health: extras.health || normalizeGridHealth({ ...source, ...extras, enabled: extras.enabled ?? source.enabled ?? source.isActive }),
    lastRunAt: extras.lastRunAt ?? source.lastRunAt ?? source.lastSuccessfulRunAt ?? source.lastSyncAt ?? null,
    lastSuccessAt: extras.lastSuccessAt ?? source.lastSuccessAt ?? source.lastSuccessfulRunAt ?? null,
    lastFailureAt: extras.lastFailureAt ?? source.lastFailureAt ?? null,
    lastErrorMessage: extras.lastErrorMessage ?? source.lastErrorMessage ?? source.lastSyncError ?? null,
    nextRunAt: extras.nextRunAt ?? source.nextRunAt ?? null,
    scheduleEnabled: extras.scheduleEnabled ?? source.scheduleEnabled ?? false,
    scheduleIntervalMinutes: extras.scheduleIntervalMinutes ?? source.scheduleIntervalMinutes ?? null,
    supportsSchedule: extras.supportsSchedule ?? source.supportsSchedule !== false,
    supportsRunNow: extras.supportsRunNow ?? source.supportsRunNow !== false,
    collectorImplemented: extras.collectorImplemented ?? source.collectorImplemented !== false,
    collectorWarning: extras.collectorWarning ?? source.collectorWarning,
    latestRun: latest,
    recordsFound:
      extras.recordsFound ??
      latest?.recordsFound ??
      latest?.recordsReceived ??
      source.latestRecordsReceived ??
      source.recordsLastSeen ??
      0,
    recordsCreated:
      extras.recordsCreated ??
      latest?.recordsCreated ??
      latest?.recordsInserted ??
      source.latestRecordsInserted ??
      0,
    recordsUpdated: extras.recordsUpdated ?? latest?.recordsUpdated ?? source.latestRecordsUpdated ?? 0,
    extraSourceLines: extras.extraSourceLines || [],
    domainId: extras.domainId || source.domainId || null,
    domainLabel: extras.domainLabel || source.domainLabel || null,
    disabledRunNowReason: extras.disabledRunNowReason ?? source.disabledRunNowReason ?? null,
    disabledScheduleReason: extras.disabledScheduleReason ?? source.disabledScheduleReason ?? null,
    formatDuration,
  };
}

export function mapRealEstateSourceToGridRow(source) {
  const latest = source.latestRun || {};
  const latestStatus = String(latest.status || source.lastStatus || "").toLowerCase();
  const isRunning = Boolean(
    source.isRunning ||
      source.activeRunId ||
      latestStatus === "running" ||
      latestStatus === "queued" ||
      latestStatus === "started"
  );
  return withCommonFields(source, {
    sourceKey: source.sourceKey,
    displayName: source.displayName,
    enabled: source.enabled,
    isRunning,
    supportsStop: Boolean(source.supportsStop ?? isRunning),
    runStartedAt: source.runStartedAt || latest.startedAt || null,
    activeRunId: source.activeRunId || null,
    latestRun: latest.id || latest.runId ? latest : null,
    recordsFound: latest.recordsReceived ?? 0,
    recordsCreated: latest.recordsInserted ?? 0,
    recordsUpdated: latest.recordsUpdated ?? 0,
    extraSourceLines: [],
    domainId: "real_estate",
    domainLabel: "Real Estate",
  });
}

export function mapWeatherSourceToGridRow(source) {
  const health = source.latestHealth;
  return withCommonFields(source, {
    sourceKey: source.sourceCode,
    displayName: source.sourceName,
    sourceCode: source.sourceCode,
    enabled: source.isActive,
    attribution: source.attributionText,
    sourceUrl: source.providerUrl,
    lastErrorMessage: source.lastErrorMessage,
    latestRun: health
      ? {
          status: health.status,
          startedAt: health.checkedAt,
          durationMs: health.latencyMs,
          recordsFound: health.recordsReceived ?? 0,
          recordsCreated: 0,
          recordsUpdated: 0,
        }
      : null,
    recordsFound: health?.recordsReceived ?? 0,
    extraSourceLines: source.sourceCategory ? [source.sourceCategory] : [],
    domainId: "weather",
    domainLabel: "Weather",
    supportsRunNow: source.supportsRunNow !== false,
    supportsSchedule: source.supportsSchedule !== false,
    disabledRunNowReason:
      source.supportsRunNow === false
        ? "This weather source does not support a manual refresh."
        : null,
    disabledScheduleReason:
      source.supportsSchedule === false
        ? "Scheduling is not supported for this weather source."
        : null,
  });
}

export function mapDomainHealthSourceToGridRow(source, extras = {}) {
  const extraLines = extras.extraLines || [];
  return withCommonFields(source, {
    sourceKey: source.sourceCode || source.sourceKey,
    displayName: source.sourceName,
    enabled: source.isActive ?? source.enabled,
    lastRunAt: source.lastSuccessfulRunAt || source.latestRunCompletedAt || source.lastRunAt,
    lastSuccessAt: source.lastSuccessfulRunAt,
    lastErrorMessage: source.latestErrorMessage || source.lastErrorMessage,
    latestRun: source.latestRunStatus
      ? {
          status: source.latestRunStatus,
          startedAt: source.latestRunStartedAt,
          recordsFound: source.latestRecordsReceived ?? 0,
          recordsCreated: source.latestRecordsInserted ?? 0,
          recordsUpdated: source.latestRecordsUpdated ?? 0,
        }
      : null,
    extraSourceLines: extraLines,
    ...extras,
  });
}

export function mapFoodSourceToGridRow(source) {
  const group = foodSourceGroupLabel(source.sourceGroup) || source.sourceGroup;
  const details = foodSourceDetailLines(source);
  const hasRunner = foodSourceHasAutomatedRunner(source);
  return mapDomainHealthSourceToGridRow(source, {
    extraLines: [group, ...details].filter(Boolean),
    domainId: "food",
    domainLabel: "Food",
    supportsRunNow: hasRunner,
    supportsSchedule: true,
    disabledRunNowReason: hasRunner
      ? null
      : "No automated runner is mapped to this food data source yet. You can still save a schedule; due runs will stay blocked until a runner is registered.",
  });
}

export function mapTransportSourceToGridRow(source) {
  const meta = source.transportMetadata || {};
  const details = transportSourceDetailLines(source);
  const hasJob = Boolean(source.transportJobKey);
  return mapDomainHealthSourceToGridRow(source, {
    extraLines: [
      meta.enablementStatusLabel || meta.enablementStatus,
      meta.readinessTier,
      meta.recommendedNextAction,
      ...details,
    ].filter(Boolean),
    domainId: "transport",
    domainLabel: "Transport",
    supportsSchedule: true,
    supportsRunNow: hasJob,
    disabledRunNowReason: hasJob
      ? null
      : "No automated transport scheduler job is mapped to this data source yet.",
  });
}

function cultureRunNowUnavailableReason(source) {
  if (source.referenceOnly) {
    return "Reference-only source — use the curated seed workflow instead of Run Now.";
  }
  if (source.moderationBased) {
    return "Moderation-based source — review submissions in the Culture moderation queue.";
  }
  if (source.blockingReasons?.length) {
    return source.blockingReasons.join("; ");
  }
  if (source.canRunNow === false) {
    return source.recommendedNextAction || "This culture source cannot be run yet.";
  }
  return null;
}

export function mapCultureSourceToGridRow(source) {
  const disabledRunNowReason = cultureRunNowUnavailableReason(source);
  return withCommonFields(source, {
    sourceKey: source.sourceKey || source.sourceCode,
    displayName: source.sourceName,
    enabled: source.enabled ?? source.isActive,
    lastRunAt: source.lastRunAt,
    lastSuccessAt: source.lastSuccessAt,
    lastErrorMessage: source.lastErrorMessage,
    latestRun: source.latestRunStatus
      ? {
          status: source.latestRunStatus,
          startedAt: source.lastRunAt,
          recordsFound: source.latestRecordsFound ?? source.recordsLastSeen ?? 0,
          recordsCreated: source.latestRecordsInserted ?? 0,
          recordsUpdated: source.latestRecordsUpdated ?? 0,
        }
      : null,
    extraSourceLines: [],
    domainId: "culture",
    domainLabel: "Culture",
    supportsSchedule: true,
    supportsRunNow: !disabledRunNowReason,
    disabledRunNowReason,
  });
}

export function mapVendorDirectorySourceToGridRow(source) {
  return mapDomainHealthSourceToGridRow(source, {
    extraLines: [
      source.connectorKey ? `Connector: ${source.connectorKey}` : "No automated connector",
      source.legalUseStatus ? `Legal use: ${source.legalUseStatus}` : null,
    ].filter(Boolean),
    domainId: "vendor_directory",
    domainLabel: "Vendors",
    supportsRunNow: source.supportsRunNow !== false && Boolean(source.connectorKey),
    supportsSchedule: false,
    disabledRunNowReason:
      source.disabledRunNowReason ||
      (source.connectorKey ? null : "This source is not collected automatically."),
    disabledScheduleReason: "Vendor directory collection uses CasaMapa collection plans.",
  });
}

export function mapFinanceSourceToGridRow(source) {
  return withCommonFields(source, {
    sourceKey: source.sourceKey,
    displayName: source.sourceName,
    enabled: source.enabled ?? source.isActive ?? true,
    lastRunAt: source.fetchedAt || source.lastRun?.finishedAt || source.lastRunAt,
    lastErrorMessage: source.snapshotError || source.lastRun?.reason || source.lastErrorMessage,
    latestRun: source.lastRun
      ? {
          status: source.lastRun.status,
          startedAt: source.lastRun.finishedAt,
        }
      : null,
    extraSourceLines: [
      source.provider ? `Provider: ${source.provider}` : null,
      source.scheduleNote,
    ].filter(Boolean),
    domainId: "finance",
    domainLabel: "Finance",
    supportsSchedule: true,
    supportsRunNow: true,
  });
}

export function mapGa4SourceToGridRow(source) {
  return withCommonFields(source, {
    sourceKey: source.sourceKey,
    displayName: source.sourceName,
    enabled: source.enabled,
    scheduleEnabled: Boolean(source.enabled && source.pollIntervalMinutes),
    scheduleIntervalMinutes: source.pollIntervalMinutes ?? null,
    lastRunAt: source.lastSyncAt,
    lastErrorMessage: source.lastSyncError,
    latestRun: source.lastSyncAt
      ? {
          status: source.lastSyncStatus,
          startedAt: source.lastSyncAt,
          recordsFound: source.rowsFetched ?? 0,
        }
      : source.latestRun,
    recordsFound: source.rowsFetched ?? 0,
    extraSourceLines: [
      source.clientViewSlug ? `Client View: ${source.clientViewSlug}` : null,
      source.measurementId,
      source.propertyId ? `Property: ${source.propertyId}` : null,
    ].filter(Boolean),
    domainId: "analytics",
    domainLabel: "Analytics",
    supportsSchedule: true,
    supportsRunNow: Boolean(source.canConfigure && source.propertyId && source.credentialsPresent),
    disabledRunNowReason: !source.canConfigure
      ? "Client View record required before GA4 sync can run."
      : !source.propertyId
        ? "Set the GA4 property ID in source settings before running."
        : !source.credentialsPresent
          ? "Configure the server GA4 service account credentials before running."
          : null,
  });
}

export function mapVehicleMarketSourceToGridRow(source) {
  const compliance = deriveComplianceDisplay(source);
  const registry = deriveRegistryBadge(source);
  const health = normalizeVehicleMarketHealthKey(source);
  const actions = deriveVehicleMarketActionState(source, { canManageSources: true });
  const isRunning = Boolean(
    source.isRunning ||
      health === "running" ||
      String(source.lastRunStatus || source.lastStatus || "").toLowerCase() === "running"
  );
  return withCommonFields(source, {
    sourceKey: source.id || source.automotiveSourceKey || source.vehicleSourceKey,
    displayName: source.displayName,
    sourceCode: source.automotiveSourceKey || source.vehicleSourceKey || source.id,
    enabled: source.isActive,
    health,
    isRunning,
    supportsStop: Boolean(source.supportsStop ?? isRunning),
    lastRunAt: source.lastRunAt,
    lastRunStatus: source.lastRunStatus || source.lastStatus || null,
    lastErrorMessage: source.lastErrorMessage || source.runBlocker,
    recordsFound: source.lastRecordCount ?? source.activeListingCount ?? 0,
    supportsSchedule: Boolean(actions.schedule.modalAllowed),
    supportsRunNow: Boolean(actions.runNow.enabled),
    disabledRunNowReason: actions.runNow.disabledReason,
    disabledScheduleReason: actions.schedule.disabledReason,
    extraSourceLines: [
      formatSourceTypeLabel(source),
      registry?.label ? `Registry: ${registry.label}` : null,
      compliance?.label ? `Compliance: ${compliance.label}` : null,
      `Listings: ${listingsDisplay(source)}`,
    ].filter(Boolean),
    domainId: "vehicle_market",
    domainLabel: "Vehicle Market",
    minimumScheduleIntervalMinutes:
      source.scheduleLimited && source.scheduleIntervalMinutes
        ? Number(source.scheduleIntervalMinutes)
        : null,
  });
}
